/**
 * Dónde se recuerda en qué organización trabaja cada persona.
 *
 * ── QUÉ ES ESTO, Y QUÉ NO ES ────────────────────────────────────────────────
 * ⚠️ CORRECCIÓN (17-sep-2026). Este módulo nació con una justificación EQUIVOCADA: que
 * el invitado no tiene `clientes/{uid}` y por eso su elección no se podía guardar. Falso.
 * `server/src/routes/organizationRoutes.js:1160-1170` hace
 * `clienteRef.set({ organizationContext: { currentOrgId, … } }, { merge: true })` al aceptar
 * una invitación, y un `set` con merge CREA el documento si no existe. Verificado sobre los
 * datos: el invitado de la prueba del 16-sep tiene un `clientes/{uid}` de 6 claves con su
 * `currentOrgId` puesto.
 *
 * Así que esto es un RESPALDO, no la pieza que sostiene el caso del invitado. Firestore es
 * la fuente y casi siempre tiene el valor bueno. Lo que cubre este módulo es el hueco que
 * queda cuando la escritura de Firestore NO ocurre:
 *
 *   - `switchContext` sólo escribe si `snap.exists()` (`OrganizationContext.jsx:223`), y no
 *     comprueba de nuevo; el `updateDoc` de `loadOrgContext` LANZA si el documento no está.
 *   - cualquiera de las dos puede fallar por red, y las dos son fire-and-forget o van dentro
 *     de un `catch` que sólo registra.
 *
 * En esos casos la preferencia sobrevive en el navegador en vez de perderse. Es poco, y es
 * justo lo que se pretende: una red de seguridad barata, no un segundo sistema de verdad.
 *
 * ── POR QUÉ LA CLAVE LLEVA EL uid ───────────────────────────────────────────
 * Porque `localStorage` es del NAVEGADOR, no de la sesión. Con una clave fija, dos personas
 * que compartan un ordenador —y en un negocio pequeño eso es lo normal— heredarían la
 * elección de la otra: la segunda entraría en una organización que quizá ni ve, y si la ve,
 * en la que no eligió. Con el uid dentro de la clave cada cuenta tiene su casilla y no hay
 * nada que aislar al cerrar sesión.
 *
 * Por eso mismo el cierre de sesión NO borra: es una preferencia por usuario y navegador,
 * como el idioma. Borrarla obligaría a reelegir a quien entra y sale de su propia cuenta.
 *
 * ── LO QUE PUEDE FALLAR ─────────────────────────────────────────────────────
 * `localStorage` lanza en modo privado de Safari y con las cookies de sitio bloqueadas.
 * Todo va en try/catch y el fallo devuelve `null`: la preferencia se pierde, que es un
 * inconveniente, y nunca una excepción durante la resolución de la sesión, que sería una
 * pantalla en blanco.
 */

const PREFIJO = 'pux:orgElegida:';

/**
 * La clave de este usuario. Exportada porque es lo que hay que probar: el aislamiento
 * entre cuentas del mismo navegador es una propiedad de la clave, no del código que la usa.
 */
function claveDeOrgElegida(uid) {
  if (!uid) return null;
  return PREFIJO + uid;
}

// El almacén se inyecta para poder ejercitar esto en la suite de `functions/`, que corre en
// Node y no tiene `localStorage`. En el navegador el valor por defecto es el de siempre.
function almacenPorDefecto() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function leerOrgElegida(uid, almacen = almacenPorDefecto()) {
  const clave = claveDeOrgElegida(uid);
  if (!clave || !almacen) return null;
  try {
    const v = almacen.getItem(clave);
    return v || null;
  } catch {
    return null;
  }
}

function guardarOrgElegida(uid, orgId, almacen = almacenPorDefecto()) {
  const clave = claveDeOrgElegida(uid);
  if (!clave || !orgId || !almacen) return;
  try {
    almacen.setItem(clave, orgId);
  } catch {
    /* modo privado o cookies bloqueadas: se pierde la preferencia, no la sesión */
  }
}

function olvidarOrgElegida(uid, almacen = almacenPorDefecto()) {
  const clave = claveDeOrgElegida(uid);
  if (!clave || !almacen) return;
  try {
    almacen.removeItem(clave);
  } catch {
    /* ídem */
  }
}

/**
 * Qué elección previa se le pasa a `decidirOrganizacion`.
 *
 * FIRESTORE MANDA, y el orden no es casual: lo escribe el servidor —el webhook de Stripe
 * pone ahí la organización recién creada (`functions/stripeWebhook.js:1284`)— y viaja entre
 * dispositivos. `localStorage` es el respaldo de quien no tiene `clientes/{uid}`.
 *
 * `??` y no `||`: una cadena vacía en Firestore es un valor escrito, y degradar a
 * localStorage por él sería resucitar una elección anterior que alguien ya sustituyó.
 */
function resolverOrgGuardada({ firestoreOrgId = null, localOrgId = null } = {}) {
  return firestoreOrgId ?? localOrgId ?? null;
}

module.exports = {
  claveDeOrgElegida,
  leerOrgElegida,
  guardarOrgElegida,
  olvidarOrgElegida,
  resolverOrgGuardada,
};
